/**
 * 메뉴 아이콘. 사이드바와 홈 위젯이 같은 그림을 써야 하므로 한 곳에 둔다.
 *
 * 아이콘 라이브러리를 넣지 않고 인라인 SVG 로 유지하는 것은 v1 과 같은 선택이다.
 * 쓰는 아이콘이 예닐곱 개뿐이라 의존성 하나를 더할 이유가 없다.
 */
export function MenuIcon({ name, size = 15 }: { name: string; size?: number }) {
  const s = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 2,
  };

  if (name === 'home') return <svg {...s}><path d="M3 10.5L12 3l9 7.5" /><path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" /><path d="M10 21v-6h4v6" /></svg>;
  if (name === 'calendar') return <svg {...s}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>;
  if (name === 'users') return <svg {...s}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
  if (name === 'message') return <svg {...s}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;
  if (name === 'layout') return <svg {...s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></svg>;
  /*
    환경설정 — <b>톱니바퀴</b>(사용자 지시 2026-09-18). 그 전에는 자물쇠였는데, 그 그림은
    「설정」이 아니라 「잠김」으로 읽히고 아래 `lock` 과 거의 같은 모양이었다.

    <p>날을 여덟으로 둔 <b>단순한</b> 톱니다. 흔한 페더(feather) 톱니는 바깥선이 곡선 스무 개라
    실제로 쓰는 15px 에서 뭉개져 덩어리로 보인다 — 이 집합의 나머지(사각·선)와도 결이 맞지 않는다.
  */
  if (name === 'settings') return <svg {...s}><circle cx="12" cy="12" r="3" /><path d="M10.3 2.8h3.4l.4 2.6 2.2 1.3 2.4-1.1 1.7 3-2 1.7v2.4l2 1.7-1.7 3-2.4-1.1-2.2 1.3-.4 2.6h-3.4l-.4-2.6-2.2-1.3-2.4 1.1-1.7-3 2-1.7v-2.4l-2-1.7 1.7-3 2.4 1.1 2.2-1.3z" /></svg>;
  if (name === 'mail') return <svg {...s}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>;
  // 자료실 — 서류철. 최상위 아홉 줄이 서로 다른 그림을 갖도록 2026-09-18 에 더했다(그 전에는
  // 전자결재·자료실·자산관리가 모두 기본값인 달력이었다).
  if (name === 'folder') return <svg {...s}><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>;
  // 자산관리 — 상자. 차량·비품·소모품처럼 <b>세어서 관리하는 물건</b>이다.
  if (name === 'box') return <svg {...s}><path d="M12 2l9 5v10l-9 5-9-5V7z" /><path d="M3 7l9 5 9-5M12 12v10" /></svg>;
  /*
    총무 관리 — <b>서류가방</b>(사용자 지시 2026-10-22). 바로 위 자산관리의 상자(마름모 덩어리)와
    가로로 넓은 몸통 · 위의 손잡이로 갈린다. 몸통의 가로줄 하나가 뚜껑 이음이다 — 줄을 더 그으면
    15px 에서 서류철(`folder`)·달력과 덩어리가 같아진다.
  */
  if (name === 'briefcase') return <svg {...s}><rect x="2.5" y="7" width="19" height="13" rx="2" /><path d="M9 7V5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 5v2" /><path d="M2.5 12.5h19" /></svg>;
  // 원격지원 — <b>모니터</b>(DOC-137). 화면과 받침대. 서류가방과는 받침대 다리로 갈린다.
  if (name === 'monitor') return <svg {...s}><rect x="2.5" y="4" width="19" height="12.5" rx="1.5" /><path d="M12 16.5V20" /><path d="M8 20h8" /></svg>;
  /*
    보미기술집 — <b>펼친 책</b>(사용자 지시 2026-09-22 「아이콘 좀 수정하자」).

    그 전에는 기본값인 격자가 떴다. 격자는 「아직 정하지 않았다」 는 뜻으로 둔 그림이라
    ({@code FALLBACK_ICON}) 최상위 한 줄이 이름표 없이 서 있던 셈이다.

    <p>가운데 선 하나로 <b>펼친 것</b>임을 말한다. 접힌 책(사각형)으로 그리면 15px 에서 자료실의
    서류철·자산관리의 상자와 덩어리 모양이 같아진다 — 이 집합에서 그림은 글자보다 먼저 읽히는
    표지라, 셋이 같은 실루엣이면 표지 노릇을 못 한다.
  */
  if (name === 'book') return <svg {...s}><path d="M12 6.5C10.5 5 8.5 4.5 3 4.5V18c5.5 0 7.5.5 9 2 1.5-1.5 3.5-2 9-2V4.5c-5.5 0-7.5.5-9 2z" /><path d="M12 6.5V20" /></svg>;

  /*
    CS — <b>세로로 선 건물</b>(DOC-95). 다 지어 사람이 들어간 곳이다.

    현장 현황의 `layout`(칸 나뉜 사각)과 실루엣이 갈려야 한다 — 저쪽은 가로로 넓고
    이쪽은 세로로 길다. 창 넷으로 「집이 여럿 든 건물」을 말한다.
  */
  if (name === 'building') return <svg {...s}><path d="M6 22V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v18" /><path d="M3 22h18" /><path d="M9.5 7h1M13.5 7h1M9.5 11h1M13.5 11h1M9.5 15h1M13.5 15h1" /><path d="M10 22v-4h4v4" /></svg>;

  // 홈 위젯이 쓰는 그림.
  if (name === 'grid') return <svg {...s}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>;
  if (name === 'user') return <svg {...s}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;
  if (name === 'check') return <svg {...s}><path d="M9 11l3 3 8-8" /><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" /></svg>;
  if (name === 'bell') return <svg {...s}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>;
  if (name === 'sun') return <svg {...s}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.5 1.5M17.6 17.6l1.5 1.5M4.9 19.1l1.5-1.5M17.6 6.4l1.5-1.5" /></svg>;
  if (name === 'cloud') return <svg {...s}><path d="M17.5 19a4.5 4.5 0 0 0 .5-8.97A6 6 0 0 0 6.3 10.4 3.8 3.8 0 0 0 7 19z" /></svg>;
  if (name === 'rain') return <svg {...s}><path d="M17.5 16a4.5 4.5 0 0 0 .5-8.97A6 6 0 0 0 6.3 7.4 3.8 3.8 0 0 0 7 16" /><path d="M8 19l-1 3M13 19l-1 3M18 19l-1 3" /></svg>;
  if (name === 'clipboard') return <svg {...s}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /></svg>;
  if (name === 'chart') return <svg {...s}><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></svg>;
  if (name === 'food') return <svg {...s}><path d="M4 2v7a3 3 0 0 0 6 0V2M7 12v10" /><path d="M18 2c-1.7 1.6-2.5 3.7-2.5 6.2 0 2 .8 3.3 2.5 3.3V22" /></svg>;
  if (name === 'news') return <svg {...s}><path d="M4 22a2 2 0 0 1-2-2V11h4" /><path d="M6 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H4" /><path d="M10 7h8M10 11h8M10 15h5" /></svg>;
  if (name === 'plus') return <svg {...s}><path d="M12 5v14M5 12h14" /></svg>;
  if (name === 'x') return <svg {...s}><path d="M18 6L6 18M6 6l12 12" /></svg>;
  if (name === 'drag') return <svg {...s}><path d="M8 7h8M8 12h8M8 17h8" /></svg>;
  if (name === 'lock') return <svg {...s}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>;
  // 「눌러서 갈 곳이 있다」 는 표시. 홈 인사말이 다음 걸음을 가리킬 때 쓴다(DOC-66 §8).
  if (name === 'chevron-right') return <svg {...s}><path d="M9 6l6 6-6 6" /></svg>;
  // 「펴고 접는다」 는 표시. 돌려서 접힘 상태를 함께 나타낸다(경력관리의 인력 목록).
  if (name === 'chevron-down') return <svg {...s}><path d="M6 9l6 6 6-6" /></svg>;
  // 글씨 크기. 위젯 관리의 글씨 단 고르는 자리가 쓴다.
  if (name === 'type') return <svg {...s}><path d="M4 7V4h16v3M9 20h6M12 4v16" /></svg>;
  // 「이 자리에 박는다」. 홈 배치 커스텀이 위젯 자리를 고정할 때 쓴다 — 자물쇠(lock)와 나눈 이유는
  // 저쪽이 「못 끈다」 는 권한 표시라서다. 이건 사용자가 자기 손으로 박고 뽑는다.
  if (name === 'pin') return <svg {...s}><path d="M12 17v5" /><path d="M9 3h6l-1 6 3 3v2H7v-2l3-3z" /></svg>;
  // 「여기서 고친다」. 위젯 머리에 붙어 그 자리에서 입력창을 여는 버튼이 쓴다(주단위 식단표).
  if (name === 'pencil') return <svg {...s}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>;
  /*
    연락처 — <b>수화기</b>. 현장 현황 머리 줄의 「연락처」 입구가 쓴다.

    <p>사람(`users`)으로 그리지 않은 이유: 그 그림은 사이드바의 인사 메뉴가 이미 쓰고 있어,
    같은 화면 안에서 두 곳을 가리키는 표지가 된다. 저 표에서 실제로 찾는 것은 사람이 아니라
    <b>번호</b>다.

    <p>흔한 페더 수화기는 곡선이 열둘이라 14px 에서 뭉갠다. 꺾인 자리를 셋으로 줄여
    실루엣만 남겼다 — 이 집합의 나머지와 굵기가 같아야 한 줄에 섰을 때 결이 맞는다.
  */
  if (name === 'phone') return <svg {...s}><path d="M21 16.5v3a2 2 0 0 1-2.2 2A18 18 0 0 1 2.5 5.2 2 2 0 0 1 4.5 3h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.6a2 2 0 0 1-.5 2.1L8.5 10.5a15 15 0 0 0 5 5l1.1-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.6 2.6.7a2 2 0 0 1 1.7 2z" /></svg>;

  /*
    아래 열셋은 <b>묶음 탭</b>이 쓰는 그림이다({@link tabIconOf}, 사용자 지시 2026-09-29
    「아이콘 + 라벨」). 최상위 아홉과 달리 이쪽은 <b>같은 줄의 형제끼리만</b> 갈리면 된다 —
    한 화면에 서는 것이 한 묶음의 자식뿐이라, 책(`book`)이 게시판과 보미기술집에 겹쳐 서도
    두 그림이 나란히 보이는 날은 없다.

    <p>그래서 여기 더한 것도 <b>모자란 만큼</b>이다. 탭 마흔 자리에 마흔 개의 새 그림을 그리면
    절반은 서로 구별되지 않는 선 덩어리가 된다 — 15px 에서 읽히는 실루엣의 수는 그보다 적다.
  */
  /*
    진행 현장 — <b>안전모</b>(사용자 지시 2026-09-29). 짓고 있는 곳의 물건이다.

    <p>네 번 고쳐 여기 왔다. 값이 셋이고, 셋 다 <b>그림 하나가 무엇으로 오독되는가</b>로 정해졌다.
    <ul>
      <li><b>볏은 넓게</b> — 좁은 꼭지로 얹으면 벨(종)의 손잡이가 된다. 챙만큼은 아니어도 돔 폭의
          절반은 되어야 «모자 위에 난 등뼈» 로 읽힌다.
      <li><b>챙은 돔보다 내밀게</b> — 챙(2~22)이 돔(6.2~17.8)보다 양쪽으로 4px 씩 나온다.
          안전모를 안전모로 만드는 것은 이 내밂 하나다.
      <li><b>돔 안에 선을 긋지 않는다</b> — 가운데 심줄이나 가로 이음선을 넣으면 정자(亭子)·아치가
          된다. 15px 에서 돔 속의 선은 구조물의 기둥으로 보인다.
    </ul>

    <p>같은 줄의 준공(`building`, 세로로 긴 건물)·입찰(`clipboard`, 세로 서류판)과 이 그림이
    <b>가로로 넓다</b>는 점에서 갈린다 — 형제끼리만 갈리면 된다는 것이 탭 그림표의 규칙이다
    ({@code tabIcons.ts}).
  */
  if (name === 'hardhat') return <svg {...s}><path d="M2 18.4h20" /><path d="M6.2 18.4V13a5.8 5.8 0 0 1 11.6 0v5.4" /><path d="M8.9 9.6V6.6a1.5 1.5 0 0 1 1.5-1.5h3.2a1.5 1.5 0 0 1 1.5 1.5v3" /></svg>;
  // 월간업무 — 달력 안에 줄이 찬 것. 빈 달력(`calendar`)이 주간이라 둘이 한 줄에 선다.
  if (name === 'calendar-days') return <svg {...s}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="M7 14h3M13 14h4M7 18h6" /></svg>;
  if (name === 'clock') return <svg {...s}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></svg>;
  if (name === 'power') return <svg {...s}><path d="M12 3v9" /><path d="M6.7 6.7a8 8 0 1 0 10.6 0" /></svg>;
  if (name === 'file') return <svg {...s}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></svg>;
  // 명함 — 가로 카드에 얼굴과 줄. 증명서(`file`)와 세로·가로로 갈린다.
  if (name === 'card') return <svg {...s}><rect x="2.5" y="5" width="19" height="14" rx="2" /><circle cx="8" cy="11" r="2" /><path d="M14 10h4M14 14h4M5.5 15.5c.6-1.2 1.6-1.8 2.5-1.8s1.9.6 2.5 1.8" /></svg>;
  if (name === 'search') return <svg {...s}><circle cx="11" cy="11" r="7" /><path d="M16.2 16.2L21 21" /></svg>;
  if (name === 'upload') return <svg {...s}><path d="M12 16V4" /><path d="M8 8l4-4 4 4" /><path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></svg>;
  if (name === 'flask') return <svg {...s}><path d="M9 2h6" /><path d="M10 2v6.5L4.6 18a2 2 0 0 0 1.7 3h11.4a2 2 0 0 0 1.7-3L14 8.5V2" /><path d="M7.3 15h9.4" /></svg>;
  if (name === 'shield') return <svg {...s}><path d="M12 2l8 3.5v6c0 5-3.4 8.9-8 10.5-4.6-1.6-8-5.5-8-10.5v-6z" /></svg>;
  if (name === 'star') return <svg {...s}><path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z" /></svg>;
  if (name === 'link') return <svg {...s}><path d="M10 13a5 5 0 0 0 7.1 0l2.9-2.9a5 5 0 0 0-7.1-7.1L11.2 4.7" /><path d="M14 11a5 5 0 0 0-7.1 0L4 13.9a5 5 0 0 0 7.1 7.1l1.6-1.6" /></svg>;
  if (name === 'key') return <svg {...s}><circle cx="7.5" cy="15.5" r="4" /><path d="M10.4 12.6L20 3" /><path d="M16.5 6.5l2.5 2.5M14 9l2.5 2.5" /></svg>;

  return null;
}
